import { Form } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { GeneralError } from '#/components/Form/GeneralError';
import { InputField } from '#/components/Form/InputField';
import { RecaptchaField } from '#/components/Form/RecaptchaField';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { useLoggedIn, useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { Modal } from '#/modals/Modal';
import type { RegisterForEventPayload } from '#/modules/listing/events/api/listingApiRegisterForEvent';
import { listingApiRegisterForEvent } from '#/modules/listing/events/api/listingApiRegisterForEvent';
import type { ApiOwnRsvp } from '#/modules/listing/events/api/types/ApiRsvpResponse';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

type Props = {
  eventId: string;
  onRegistered: (ownRsvp: ApiOwnRsvp) => void;
  onClose: () => void;
};

export function RsvpRegistrationModal({
  eventId,
  onRegistered,
  onClose,
}: Props) {
  const { i18n } = useI18n();
  const { user } = useUser();
  const { loggedIn } = useLoggedIn();

  const initialValues: RegisterForEventPayload = {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    email: user?.email ?? '',
  };

  const register: FormSubmitFunction<RegisterForEventPayload> = async ({
    values,
    handleError,
  }) => {
    try {
      const { ownRsvp } = await listingApiRegisterForEvent(eventId, values);
      trackClicked('Register for event', { listing_id: eventId });
      onRegistered(ownRsvp);
      onClose();
    } catch (error) {
      handleError(error as Error);
    }
  };

  return (
    <Modal title={i18n('Your Info')} onClose={onClose} qaId="rsvp-modal">
      <Form
        analyticsTitle="Event registration modal"
        usageContext="modal"
        qaId="rsvp-form"
        initialValues={initialValues}
        onSubmit={register}
        render={() => (
          <>
            <GeneralError />

            <InputField
              name="firstName"
              label={`${i18n('First Name')}*`}
              placeholder={i18n('Casey')}
            />

            <InputField
              name="lastName"
              label={`${i18n('Last Name')}*`}
              placeholder={i18n('Smith')}
            />

            <InputField
              name="email"
              label={`${i18n('Email')}*`}
              placeholder={i18n('you@email.com')}
            />

            {!loggedIn && <RecaptchaField />}

            <SubmitButton>
              {
                /* TRANSLATORS: event registration modal CTA */
                i18n('Register')
              }
            </SubmitButton>
          </>
        )}
      />
    </Modal>
  );
}
